/** Strings of the customer screens (list, new, edit, profile) and the recurring-costs (webhosting) section: `customersEn` is the source, `customersCs` must have the same keys. */
export const customersEn = {
  customers: {
    list: {
      title: "Customers",
      total_one: "{{count}} customer total",
      total_few: "{{count}} customers total",
      total_other: "{{count}} customers total",
      add: "Add Customer",
      searchPlaceholder: "Search customers...",
      empty: "No customers yet.",
      columns: {
        name: "Name",
        fakturoidId: "Fakturoid ID",
        projects: "Projects",
        invoices: "Invoices",
        created: "Created",
      },
    },
    new: {
      title: "New Customer",
      subtitle: "Add a new customer to the system",
      detailsTitle: "Customer Details",
      detailsHint: "Fill in the customer information below.",
      name: "Name",
      namePlaceholder: "Customer name",
      fakturoidSubjectId: "Fakturoid Subject ID",
      optional: "Optional",
      creating: "Creating…",
      create: "Create Customer",
      cancel: "Cancel",
      createFailed: "Couldn't create the customer.",
      nameRequired: "Name is required.",
    },
    edit: {
      title: "Edit {{name}}",
      subtitle: "Customer settings",
      backToProfile: "Back to profile",
      infoTitle: "Customer information",
      infoHint: "Update the customer's name and integration bindings.",
      dangerTitle: "Danger Zone",
      dangerHint: "Permanently delete this customer and all associated data.",
      name: "Name",
      fakturoidSubjectId: "Fakturoid Subject ID",
      saving: "Saving",
      save: "Save Changes",
      created: "Created",
      updated: "Customer updated",
      updateFailed: "Update failed",
      nameRequired: "Name is required",
      deleted: "Customer deleted",
      deleteFailed: "Delete failed",
      typeDelete: "Type DELETE to confirm",
      deleting: "Deleting",
      delete: "Delete Customer",
    },
    profile: {
      subtitle: "Customer profile",
      billsThrough: " · bills through ",
      edit: "Edit customer",
      back: "Back to Customers",
      stats: {
        projects: "Projects",
        workItems: "Work items",
        invoiceBatches: "Invoice batches",
        totalPaid: "Total paid",
      },
      projects: {
        title: "Projects",
        count_one: "{{count}} project",
        count_few: "{{count}} projects",
        count_other: "{{count}} projects",
        empty: "No projects yet.",
        name: "Name",
        gitlabId: "GitLab ID",
        workItems: "Work Items",
      },
      costs: {
        title: "Billing Costs",
        hint: "One-off costs picked per report and consumed once invoiced. (Recurring costs like webhosting are managed in the section above.)",
        label: "Label",
        labelPlaceholder: "Webhosting",
        amountCzk: "Amount (CZK)",
        amount: "Amount",
        state: "State",
        adding: "Adding",
        add: "Add cost",
        remove: "Remove",
        invoiced: "Invoiced",
        pending: "Pending",
        empty: "No billing costs configured.",
        added: "Cost added",
        addFailed: "Add failed",
        removed: "Cost removed",
        removeFailed: "Delete failed",
        required: "Label and a positive amount are required",
      },
      batches: {
        title: "Invoice Batches",
        count_one: "{{count}} batch",
        count_few: "{{count}} batches",
        count_other: "{{count}} batches",
        empty: "No invoice batches yet.",
        period: "Period",
        status: "Status",
        invoiceNumber: "Invoice #",
        amount: "Amount",
        created: "Created",
      },
      invoices: {
        title: "Invoices",
        count_one: "{{count}} invoice in the accounting",
        count_few: "{{count}} invoices in the accounting",
        count_other: "{{count}} invoices in the accounting",
        empty: "No invoices issued to this customer yet.",
        number: "Invoice #",
        status: "Status",
        issued: "Issued",
        due: "Due",
        paid: "Paid",
        total: "Total",
      },
      te: {
        title: "Time Entries",
        summary: "{{count}} entries · {{hours}} h · {{amount}} total",
        byApproval: "By status (employee)",
        byBilling: "By billing (customer)",
        state: "State",
        entries: "Entries",
        hours: "Hours",
        amount: "Amount",
        approval: {
          worked: "Worked",
          budget: "Budget",
          approved: "Approved",
          paid: "Paid out",
          rejected: "Rejected",
        },
        billing: {
          unbilled: "Unbilled",
          to_invoice: "To invoice",
          invoiced: "Invoiced",
          paid: "Invoice paid",
          non_billable: "Non-billable",
          fixed_price: "Fixed price",
        },
      },
    },
    webhosting: {
      title: "Recurring costs",
      cancel: "Cancel",
      add: "Add recurring cost",
      loading: "Loading…",
      empty: "No recurring costs set up for this customer.",
      defaultLabel: "Webhosting",
      added: "Recurring cost added",
      addFailed: "Could not add",
      addInvalid: "Enter a label and a valid monthly price",
      labelPlaceholder: "Label",
      labelAria: "Recurring cost label",
      validFromAria: "Price valid from",
      monthlyPrice: "Monthly price",
      parametersPlaceholder: "Parameters — e.g. 20 GB storage, 3 domains, PHP 8.3",
      parametersAria: "Recurring cost parameters",
      saving: "Saving",
      renamed: "Recurring cost renamed",
      renameFailed: "Could not rename",
      deleted: "Recurring cost deleted",
      deleteFailed: "Could not delete",
      deactivated: "Billing deactivated",
      activated: "Billing activated",
      deactivatedHint: "New reports will skip this recurring cost until you activate it again.",
      activatedHint: "New reports will bill this recurring cost again.",
      updateFailed: "Could not update",
      labelRequired: "Enter a label",
      confirmDelete:
        'Delete recurring cost "{{label}}"? Its price history and paid-months tracking are removed too. If it merely ended, use "Deactivate billing" instead to keep the history.',
      save: "Save",
      inactive: "inactive",
      perMonth: "{{price}} / month",
      noPrice: "no price for this month",
      edit: "Edit",
      renameTitle: "Rename this recurring cost",
      deactivateTitle: "Stop billing this recurring cost — new reports will skip it; history stays",
      activateTitle: "Resume billing this recurring cost on new reports",
      deactivate: "Deactivate billing",
      activate: "Activate billing",
      hideHistory: "Hide history",
      priceHistory: "Price history",
      deleteTitle: "Delete this recurring cost including its history",
      delete: "Delete",
      addPriceChange: "Add price change",
      from: "from {{month}}",
      paidMonths: "Paid months",
      markPrepaid: "Mark prepaid period",
      noMonths:
        "No month billed yet — months appear here once a report bills them or you mark a prepaid period.",
      version: {
        saved: "Price change saved",
        saveFailed: "Could not save",
        invalid: "Enter a valid monthly price",
        validFromAria: "New price valid from",
        priceAria: "New monthly price",
        parametersPlaceholder: "Parameters (optional)",
        parametersAria: "New parameters",
        save: "Save price change",
      },
      prepaid: {
        marked_one: "Marked {{count}} month as prepaid",
        marked_few: "Marked {{count}} months as prepaid",
        marked_other: "Marked {{count}} months as prepaid",
        skipped_one: "{{count}} month was already billed by a report and stayed untouched.",
        skipped_few: "{{count}} months were already billed by a report and stayed untouched.",
        skipped_other: "{{count}} months were already billed by a report and stayed untouched.",
        failed: "Could not mark",
        pickBoth: "Pick both months of the prepaid period",
        endBeforeStart: "The period must not end before it starts",
        hint: "Months in this range are marked paid and will never be added to a generated report — use it when the customer prepaid the hosting (e.g. a year up front) on a separate invoice.",
        fromAria: "Prepaid from",
        untilAria: "Prepaid until",
        marking: "Marking",
        submit: "Mark as prepaid",
      },
      month: {
        paidByHand: "Paid by hand (prepaid) — click to remove; the month becomes billable again",
        paid: "Paid — click to mark unpaid",
        paidManual: "Paid (set by hand) — click to mark unpaid",
        unpaid: "Unpaid — click to mark paid",
      },
    },
  },
};

export const customersCs: typeof customersEn = {
  customers: {
    list: {
      title: "Zákazníci",
      total_one: "{{count}} zákazník celkem",
      total_few: "{{count}} zákazníci celkem",
      total_other: "{{count}} zákazníků celkem",
      add: "Přidat zákazníka",
      searchPlaceholder: "Hledat zákazníky...",
      empty: "Zatím žádní zákazníci.",
      columns: {
        name: "Název",
        fakturoidId: "ID ve Fakturoidu",
        projects: "Projekty",
        invoices: "Faktury",
        created: "Vytvořeno",
      },
    },
    new: {
      title: "Nový zákazník",
      subtitle: "Přidat nového zákazníka do systému",
      detailsTitle: "Údaje o zákazníkovi",
      detailsHint: "Vyplňte níže informace o zákazníkovi.",
      name: "Název",
      namePlaceholder: "Název zákazníka",
      fakturoidSubjectId: "ID subjektu ve Fakturoidu",
      optional: "Volitelné",
      creating: "Vytvářím…",
      create: "Vytvořit zákazníka",
      cancel: "Zrušit",
      createFailed: "Zákazníka se nepodařilo vytvořit.",
      nameRequired: "Název je povinný.",
    },
    edit: {
      title: "Úprava: {{name}}",
      subtitle: "Nastavení zákazníka",
      backToProfile: "Zpět na profil",
      infoTitle: "Informace o zákazníkovi",
      infoHint: "Upravte název zákazníka a napojení na integrace.",
      dangerTitle: "Nebezpečná zóna",
      dangerHint: "Trvale smazat tohoto zákazníka i všechna související data.",
      name: "Název",
      fakturoidSubjectId: "ID subjektu ve Fakturoidu",
      saving: "Ukládám",
      save: "Uložit změny",
      created: "Vytvořeno",
      updated: "Zákazník upraven",
      updateFailed: "Úprava se nezdařila",
      nameRequired: "Název je povinný",
      deleted: "Zákazník smazán",
      deleteFailed: "Smazání se nezdařilo",
      typeDelete: "Pro potvrzení napište DELETE",
      deleting: "Mažu",
      delete: "Smazat zákazníka",
    },
    profile: {
      subtitle: "Profil zákazníka",
      billsThrough: " · fakturuje přes ",
      edit: "Upravit zákazníka",
      back: "Zpět na zákazníky",
      stats: {
        projects: "Projekty",
        workItems: "Tikety",
        invoiceBatches: "Fakturační dávky",
        totalPaid: "Celkem zaplaceno",
      },
      projects: {
        title: "Projekty",
        count_one: "{{count}} projekt",
        count_few: "{{count}} projekty",
        count_other: "{{count}} projektů",
        empty: "Zatím žádné projekty.",
        name: "Název",
        gitlabId: "ID v GitLabu",
        workItems: "Tikety",
      },
      costs: {
        title: "Fakturované náklady",
        hint: "Jednorázové náklady vybírané do reportu a po vyfakturování spotřebované. (Opakované náklady, např. webhosting, se spravují v sekci výše.)",
        label: "Popisek",
        labelPlaceholder: "Webhosting",
        amountCzk: "Částka (Kč)",
        amount: "Částka",
        state: "Stav",
        adding: "Přidávám",
        add: "Přidat náklad",
        remove: "Odebrat",
        invoiced: "Vyfakturováno",
        pending: "Čeká",
        empty: "Žádné fakturované náklady.",
        added: "Náklad přidán",
        addFailed: "Přidání se nezdařilo",
        removed: "Náklad odebrán",
        removeFailed: "Smazání se nezdařilo",
        required: "Popisek a kladná částka jsou povinné",
      },
      batches: {
        title: "Fakturační dávky",
        count_one: "{{count}} dávka",
        count_few: "{{count}} dávky",
        count_other: "{{count}} dávek",
        empty: "Zatím žádné fakturační dávky.",
        period: "Období",
        status: "Stav",
        invoiceNumber: "Č. faktury",
        amount: "Částka",
        created: "Vytvořeno",
      },
      invoices: {
        title: "Faktury",
        count_one: "{{count}} faktura v účetnictví",
        count_few: "{{count}} faktury v účetnictví",
        count_other: "{{count}} faktur v účetnictví",
        empty: "Tomuto zákazníkovi zatím nebyla vystavena žádná faktura.",
        number: "Č. faktury",
        status: "Stav",
        issued: "Vystaveno",
        due: "Splatnost",
        paid: "Zaplaceno",
        total: "Celkem",
      },
      te: {
        title: "Výkazy práce",
        summary: "{{count}} záznamů · {{hours}} h · celkem {{amount}}",
        byApproval: "Podle stavu (zaměstnanec)",
        byBilling: "Podle fakturace (zákazník)",
        state: "Stav",
        entries: "Záznamy",
        hours: "Hodiny",
        amount: "Částka",
        approval: {
          worked: "Odpracováno",
          budget: "Rozpočet",
          approved: "Schváleno",
          paid: "Vyplaceno",
          rejected: "Zamítnuto",
        },
        billing: {
          unbilled: "Nevyfakturováno",
          to_invoice: "K vyfakturování",
          invoiced: "Vyfakturováno",
          paid: "Faktura zaplacena",
          non_billable: "Nefakturovatelné",
          fixed_price: "Pevná cena",
        },
      },
    },
    webhosting: {
      title: "Opakované náklady",
      cancel: "Zrušit",
      add: "Přidat opakovaný náklad",
      loading: "Načítám…",
      empty: "Pro tohoto zákazníka nejsou nastavené žádné opakované náklady.",
      defaultLabel: "Webhosting",
      added: "Opakovaný náklad přidán",
      addFailed: "Přidání se nezdařilo",
      addInvalid: "Zadejte popisek a platnou měsíční cenu",
      labelPlaceholder: "Popisek",
      labelAria: "Popisek opakovaného nákladu",
      validFromAria: "Cena platná od",
      monthlyPrice: "Měsíční cena",
      parametersPlaceholder: "Parametry — např. 20 GB úložiště, 3 domény, PHP 8.3",
      parametersAria: "Parametry opakovaného nákladu",
      saving: "Ukládám",
      renamed: "Opakovaný náklad přejmenován",
      renameFailed: "Přejmenování se nezdařilo",
      deleted: "Opakovaný náklad smazán",
      deleteFailed: "Smazání se nezdařilo",
      deactivated: "Fakturace deaktivována",
      activated: "Fakturace aktivována",
      deactivatedHint: "Nové reporty tento opakovaný náklad přeskočí, dokud ho znovu neaktivujete.",
      activatedHint: "Nové reporty budou tento opakovaný náklad znovu fakturovat.",
      updateFailed: "Aktualizace se nezdařila",
      labelRequired: "Zadejte popisek",
      confirmDelete:
        "Smazat opakovaný náklad „{{label}}“? Smaže se i jeho historie cen a evidence zaplacených měsíců. Pokud pouze skončil, použijte raději „Deaktivovat fakturaci“, historie zůstane zachována.",
      save: "Uložit",
      inactive: "neaktivní",
      perMonth: "{{price}} / měsíc",
      noPrice: "pro tento měsíc bez ceny",
      edit: "Upravit",
      renameTitle: "Přejmenovat tento opakovaný náklad",
      deactivateTitle:
        "Přestat tento opakovaný náklad fakturovat — nové reporty ho přeskočí, historie zůstane",
      activateTitle: "Obnovit fakturaci tohoto opakovaného nákladu v nových reportech",
      deactivate: "Deaktivovat fakturaci",
      activate: "Aktivovat fakturaci",
      hideHistory: "Skrýt historii",
      priceHistory: "Historie cen",
      deleteTitle: "Smazat tento opakovaný náklad včetně historie",
      delete: "Smazat",
      addPriceChange: "Přidat změnu ceny",
      from: "od {{month}}",
      paidMonths: "Zaplacené měsíce",
      markPrepaid: "Označit předplacené období",
      noMonths:
        "Zatím není vyfakturován žádný měsíc — měsíce se tu objeví, jakmile je vyfakturuje report nebo označíte předplacené období.",
      version: {
        saved: "Změna ceny uložena",
        saveFailed: "Uložení se nezdařilo",
        invalid: "Zadejte platnou měsíční cenu",
        validFromAria: "Nová cena platná od",
        priceAria: "Nová měsíční cena",
        parametersPlaceholder: "Parametry (volitelné)",
        parametersAria: "Nové parametry",
        save: "Uložit změnu ceny",
      },
      prepaid: {
        marked_one: "Označen {{count}} měsíc jako předplacený",
        marked_few: "Označeny {{count}} měsíce jako předplacené",
        marked_other: "Označeno {{count}} měsíců jako předplacených",
        skipped_one: "{{count}} měsíc už byl vyfakturován reportem a zůstal beze změny.",
        skipped_few: "{{count}} měsíce už byly vyfakturovány reportem a zůstaly beze změny.",
        skipped_other: "{{count}} měsíců už bylo vyfakturováno reportem a zůstalo beze změny.",
        failed: "Označení se nezdařilo",
        pickBoth: "Vyberte oba měsíce předplaceného období",
        endBeforeStart: "Období nesmí končit dřív, než začíná",
        hint: "Měsíce v tomto rozsahu se označí jako zaplacené a už se nikdy nepřidají do vygenerovaného reportu — použijte, když zákazník hosting předplatil (např. rok dopředu) samostatnou fakturou.",
        fromAria: "Předplaceno od",
        untilAria: "Předplaceno do",
        marking: "Označuji",
        submit: "Označit jako předplacené",
      },
      month: {
        paidByHand:
          "Zaplaceno ručně (předplaceno) — kliknutím odeberete; měsíc bude opět fakturovatelný",
        paid: "Zaplaceno — kliknutím označíte jako nezaplacené",
        paidManual: "Zaplaceno (nastaveno ručně) — kliknutím označíte jako nezaplacené",
        unpaid: "Nezaplaceno — kliknutím označíte jako zaplacené",
      },
    },
  },
};
